import {
  useViewsContext,
  useDeleteView,
  useCreateView,
  useUpdateView,
  useNotificationContext,
  useGlobalizationContext,
  useTranslation,
  useGetView
} from '@cs-cms/state-management';
import { CPS_NOTIFICATION_TYPE, DEFAULT_FACETS_COUNT } from '@cs-cms/types';
import { useQueryClient } from '@tanstack/react-query';
import { ICpsDialogProps } from '../../../common/useCpsDialog';

interface UseSystemViewMutationsProps {
  systemViews: any[];
  indexId?: number;
  onDialogClose: () => void;
  onRenameDialogClose: () => void;
  showDialog: (props: ICpsDialogProps) => void;
}

export const useSystemViewMutations = ({
  systemViews,
  indexId,
  onDialogClose,
  onRenameDialogClose,
  showDialog
}: UseSystemViewMutationsProps) => {
  const { selectedView, setSelectedView } = useViewsContext();
  const queryClient = useQueryClient();
  const { mutate: deleteView } = useDeleteView();
  const { mutate: createView } = useCreateView();
  const { mutate: updateView } = useUpdateView();
  const { showToast } = useNotificationContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { data: currentViewDetails } = useGetView(selectedView, selectedLocale);

  const handleRenameViewSubmit = (viewId: number, newName: string) => {
    if (selectedView != null) {
      updateView(
        { viewId: Number(viewId), name: newName },
        {
          onSuccess: () => {
            onRenameDialogClose();
            showToast({
              type: CPS_NOTIFICATION_TYPE.SUCCESS,
              message: translate('custom.settings.views.dialog.viewRenamed')
            });
          },
          onError: () => {
            showToast({
              type: CPS_NOTIFICATION_TYPE.ERROR,
              message: translate('custom.settings.views.dialog.viewRenamedFailed')
            });
          }
        }
      );
    }
  };

  const handleDeleteView = () => {
    const currView = systemViews.find(view => view.id === selectedView);
    if (!currView || currView.isDefault) {
      return;
    }

    showDialog({
      title: translate('custom.settings.views.dialog.deleteTitle'),
      message: translate('custom.settings.views.dialog.deleteConfirmation', { viewName: currView.name }),
      confirmButtonLabel: translate('custom.common.confirm'),
      discardButtonLabel: translate('custom.common.discard'),
      onConfirm: confirmed => {
        if (confirmed && indexId != null) {
          return deleteView(
            { viewId: currView.id, indexId },
            {
              onSuccess: () => {
                showToast({
                  type: CPS_NOTIFICATION_TYPE.SUCCESS,
                  message: translate('custom.settings.views.viewDeleted')
                });
                const remainingSystemViews = systemViews.filter(v => v.id !== currView.id);
                if (setSelectedView) {
                  setSelectedView(remainingSystemViews.length > 0 ? remainingSystemViews[0].id : undefined);
                }
              }
            }
          );
        }
        return undefined;
      }
    });
  };

  const handleSetAsDefault = () => {
    const currView = systemViews.find(view => view.id === selectedView);
    if (!currView || currView.isDefault) {
      return;
    }

    showDialog({
      title: translate('custom.settings.adminCenter.systemViewManagement.setDefaultTitle'),
      message: translate('custom.settings.adminCenter.systemViewManagement.setDefaultMessage', {
        viewName: currView.name
      }),
      confirmButtonLabel: translate('custom.common.confirm'),
      discardButtonLabel: translate('custom.common.cancel'),
      onConfirm: confirmed => {
        if (confirmed) {
          updateView(
            { viewId: Number(currView.id), isDefault: true },
            {
              onSuccess: () => {
                showToast({
                  type: CPS_NOTIFICATION_TYPE.SUCCESS,
                  message: translate('custom.settings.adminCenter.systemViewManagement.setDefaultSuccess')
                });
              },
              onError: () => {
                showToast({
                  type: CPS_NOTIFICATION_TYPE.ERROR,
                  message: translate('custom.settings.adminCenter.systemViewManagement.setDefaultFailed')
                });
              }
            }
          );
        }
      }
    });
  };

  const handleCreateNewSystemView = (viewName: string) => {
    const baseView = currentViewDetails;
    if (!baseView || indexId == null) {
      showToast({
        type: CPS_NOTIFICATION_TYPE.ERROR,
        message: translate('custom.settings.views.dialog.viewCreateFailed')
      });
      return;
    }

    const transformedFields = baseView.fields?.map(field => ({
      ...field,
      facetsCount: field.facetable ? DEFAULT_FACETS_COUNT : (field.facetsCount ?? 0)
    }));

    createView(
      {
        indexId,
        name: viewName,
        fields: transformedFields,
        isDefault: false,
        isSystemView: true,
        globalFilters: baseView.globalFilters.map(filter => ({
          fieldId: filter.id,
          facetsCount: filter.facetsCount
        }))
      },
      {
        onSuccess: newViewId => {
          queryClient.invalidateQueries({ queryKey: ['views'] });
          onDialogClose();
          if (setSelectedView) {
            setSelectedView(newViewId);
          }
          showToast({
            type: CPS_NOTIFICATION_TYPE.SUCCESS,
            message: translate('custom.settings.views.dialog.viewCreated')
          });
        },
        onError: () => {
          showToast({
            type: CPS_NOTIFICATION_TYPE.ERROR,
            message: translate('custom.settings.views.dialog.viewCreateFailed')
          });
        }
      }
    );
  };

  return {
    handleRenameViewSubmit,
    handleDeleteView,
    handleSetAsDefault,
    handleCreateNewSystemView
  };
};
